import type { LicenseModel, LicenseStatsModel } from '/#/models/licensesModel'
import type { OrganizationModel } from '/#/models/organizationModel'
import type { ComputedRef, Ref } from 'vue'

import get from 'lodash-es/get'
import intersection from 'lodash-es/intersection'
import isArray from 'lodash-es/isArray'
import isNull from 'lodash-es/isNull'
import isUndefined from 'lodash-es/isUndefined'
import map from 'lodash-es/map'
import sum from 'lodash-es/sum'

import { MY_KEYWORD } from '/@/constants/common'
import { store } from '/@/store'

export const useOrganizationStore = defineStore('organizations', () => {
  const { isAnyAnalystUser, isAnyNormalUser, isAnyMsspUser } = useUserPermission()

  const accountStore = useAccountStore()

  const isStatsLoading: Ref<boolean> = ref(false)
  const isMyOrganizationLoading: Ref<boolean> = ref(false)
  const isMyLicenseLoading: Ref<boolean> = ref(false)
  const myOrganization: Ref<OrganizationModel | null> = ref(null)
  const myLicense: Ref<LicenseModel | null> = ref(null)
  const stats: Ref<Nullable<LicenseStatsModel>> = ref(null)
  const licenseSummary = ref()

  const selectedOrganization = useStorage<Recordable | null>(
    'switched-org',
    null,
    undefined,
    {
      serializer: {
        read: (v: any) => (v ? JSON.parse(v) : null),
        write: (v: any) => JSON.stringify(v),
      },
    },
  )

  /**
   * Calculate switched organization slug.
   * - Normal organization users can not select any organization.
   * - If MSSP organization switch, we use switched otherwise we use @my
   * - Analyst users can switch organization.
   */
  const organizationSlug: ComputedRef<string | undefined> = computed(() => {
    // Analyst User
    if (isAnyAnalystUser.value) return selectedOrganization.value?.code

    // Mssp User
    if (selectedOrganization.value) return selectedOrganization.value.code

    // If the mssp user itself or an organization, we use @my
    if (isAnyNormalUser.value || isAnyMsspUser.value) return MY_KEYWORD
  })

  /**
   * Calculate switched organization code.
   * - Normal organization users can not select any organization. Because they are in their own organization.
   * - If the mssp user has selected organization, we use it. If not, we use mssp's own organization
   * - Analyst users have to choose the organization.
   */
  const organizationCode: ComputedRef<string | undefined> = computed(() => {
    // If user is analyst, return selected organization code
    if (isAnyAnalystUser.value || isAnyMsspUser.value)
      return selectedOrganization.value?.code

    // If the mssp user itself or an organization, we use user organization code
    if (isAnyNormalUser.value) {
      return accountStore.user?.organization?.code
    }

    return undefined
  })

  /**
   * Analyst users must be switch organization.
   * If MSSP organization switch, we use switched otherwise we use undefined (Mssp itself)
   * Normal organization users can not select any organization. We use undefined (Organization itself)
   */
  const organizationId: ComputedRef<number | undefined> = computed(() => {
    // If user is analyst, return selected organization id
    if (isAnyAnalystUser.value) return selectedOrganization.value?.id

    // If user is mssp or analyst and switch organization, return selected organization id
    if (selectedOrganization.value) return selectedOrganization.value.id

    // If the any normal user itself or an organization, we use user organization id
    // for MSSP users, return undefined
    if (isAnyNormalUser.value) {
      return accountStore.user?.organization?.id
    }

    return undefined
  })

  const organizationName: ComputedRef<string | undefined> = computed(() => {
    if (isAnyAnalystUser.value) return selectedOrganization.value?.name

    if (selectedOrganization.value) return selectedOrganization.value.name

    return accountStore.user?.organization?.name
  })

  const organizationIdWithAllRoles: ComputedRef<number> = computed(() => {
    // If user is analyst, return selected organization id
    if (isAnyAnalystUser.value) return selectedOrganization.value?.id

    // If user is mssp or analyst and switch organization, return selected organization id
    if (selectedOrganization.value) return selectedOrganization.value.id

    // If the mssp user itself or an organization, we use user organization code
    return accountStore.user!.organization!.id
  })

  // Calculated total Asset rights.
  const totalAssetsRights: ComputedRef<number> = computed(() =>
    sum(Object.values(stats.value?.total_asset_rights || {})),
  )

  // Calculated total Asset usage.
  const totalAssetsUsage: ComputedRef<number> = computed(() =>
    sum(Object.values(stats.value?.total_asset_usages || {})),
  )

  // Calculated total user stats.
  const totalUsersRights: ComputedRef<number | undefined> = computed(
    () => stats.value?.total_count_rights.users,
  )
  const totalUsersUsage: ComputedRef<number | undefined> = computed(
    () => stats.value?.total_count_usages.users,
  )

  // Calculated total Team stats.
  const totalTeamsRights: ComputedRef<number | undefined> = computed(
    () => stats.value?.total_count_rights.teams,
  )
  const totalTeamsUsage: ComputedRef<number | undefined> = computed(
    () => stats.value?.total_count_usages.teams,
  )

  // Calculated total Organizations stats.
  const totalOrganizationsRights: ComputedRef<number | undefined> = computed(
    () => stats.value?.total_count_rights.organizations,
  )
  const totalOrganizationsUsage: ComputedRef<number | undefined> = computed(
    () => stats.value?.total_count_usages.organizations,
  )

  // Calculated total Credits stats.
  const totalCreditsRights: ComputedRef<number | undefined> = computed(
    () => stats.value?.total_count_rights.credit,
  )
  const totalCreditsUsage: ComputedRef<number | undefined> = computed(
    () => stats.value?.total_count_usages.credit,
  )
  const totalCreditsRemaining: ComputedRef<number | undefined> = computed(
    () => stats.value?.total_count_remainings?.['ias-credit'],
  )

  /**
   * @name daysUntilLicenseExpires
   * @description Computes the number of days remaining until the license expires based on the
   * `license_valid_until` date from the `licenseSummary` object. If the license
   * expiration date is not defined or invalid, it returns 0. The calculation
   * considers only the date part by resetting the time to midnight for both
   * the current date and the expiration date. The result is rounded up to
   * ensure that any partial day counts as a full day remaining.
   */
  const daysUntilLicenseExpires = computed(() => {
    const licenseValidUntilString = licenseSummary.value?.license_valid_until

    if (!licenseValidUntilString) {
      return 0
    }

    const licenseValidUntil = new Date(licenseValidUntilString)

    if (isUndefined(licenseValidUntil.getTime())) {
      return 0
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    licenseValidUntil.setHours(0, 0, 0, 0)

    const timeDiff = licenseValidUntil.getTime() - today.getTime()
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
  })

  const isVisibleLicenseExpireAlert: ComputedRef<boolean> = computed(() => {
    return !(
      isAnyAnalystUser.value ||
      daysUntilLicenseExpires.value >= 30 ||
      (isAnyMsspUser.value &&
        accountStore.user?.organization?.license_type === 'PROOF_OF_CONCEPT') ||
      (isAnyNormalUser &&
        accountStore.user?.organization?.license_type === 'PROOF_OF_CONCEPT')
    )
  })

  /**
   * @description Determines if the current context is a proof of concept license
   * True when either selected organization or user's organization has POC license
   */
  const isPocLicense = computed(() => {
    return (
      selectedOrganization.value?.license_type === 'PROOF_OF_CONCEPT' ||
      accountStore.user?.organization?.license_type === 'PROOF_OF_CONCEPT'
    )
  })

  /**
   * Analyst and MSSP user can switch organization.
   * @param organization
   */
  function switchOrganization(organization: Recordable | undefined) {
    selectedOrganization.value = organization
  }

  /**
   * Fetch organization stats.
   */
  async function fetchStats() {
    isStatsLoading.value = true
    stats.value = await organizationsLicenseStatsApi(organizationSlug.value!)
    isStatsLoading.value = false
  }

  async function fetchMyOrganization() {
    if (
      myOrganization.value ||
      isAnyAnalystUser.value ||
      accountStore.isImpersonatingOrReleasingProgressContinue
    )
      return

    isMyOrganizationLoading.value = true
    if (isNull(myOrganization.value)) {
      myOrganization.value = await organizationsDetailApi(MY_KEYWORD)
    }
    isMyOrganizationLoading.value = false
  }

  async function fetchMyLicense() {
    if (myLicense.value) return

    isMyLicenseLoading.value = true
    if (isNull(myLicense.value)) {
      myLicense.value = await licenseDetailApi(MY_KEYWORD)
    }
    isMyLicenseLoading.value = false
  }

  async function hasMyOrganizationModule(value: string | string[]) {
    await fetchMyLicense()

    const moduleCodes = map(myLicense.value?.modules, 'short_code')

    if (!isArray(value)) {
      return moduleCodes.includes(value as string) as boolean
    }
    // It means multiple modules can do this operation.
    return (intersection(value, moduleCodes) as string[]).length > 0
  }

  async function hasMyOrganizationModuleCategory(value: string | string[]) {
    await fetchMyLicense()

    const moduleCategoryCodes = map(myLicense.value?.modules, 'category')

    if (!isArray(value)) {
      return moduleCategoryCodes.includes(value as string) as boolean
    }
    // It means multiple modules can do this operation.
    return (intersection(value, moduleCategoryCodes) as string[]).length > 0
  }

  /**
   * Check has user organization the feature.
   * @param featureName
   */
  async function hasMyLicenseFeature(featureName: string) {
    // If organization is not loaded, we fetch it.
    await fetchMyLicense()

    const properties = myLicense.value?.properties
    const featureValue = get(properties, featureName)
    if (isUndefined(featureValue)) return false
    return featureValue
  }

  /**
   * Checks user's assets rights and assets usage
   * @param page E.g. 'attack-surface'
   */
  function hasAssetUsed(page: string) {
    return !(
      stats.value?.total_asset_remainings[page] &&
      stats.value.total_asset_usages[page] === 0
    )
  }

  async function fetchLicenseSummary() {
    if (!isAnyAnalystUser.value && !isUndefined(organizationSlug.value)) {
      licenseSummary.value = await organizationLicensesSummaryApi(
        isAnyMsspUser.value ? MY_KEYWORD : organizationSlug.value,
      )
    }
  }

  /**
   * Fetch stats if organizationSlug value changed.
   * We watch this because if organization switched we use new stats for new organization.
   */
  watch(
    () => organizationSlug.value,
    async () => {
      if (!isUndefined(organizationSlug.value)) await fetchStats()
      await fetchLicenseSummary()
    },
    { immediate: true },
  )

  return {
    stats,
    myLicense,
    licenseSummary,
    myOrganization,
    isStatsLoading,
    selectedOrganization,
    isMyLicenseLoading,
    isMyOrganizationLoading,
    organizationId,
    organizationIdWithAllRoles,
    organizationSlug,
    organizationCode,
    totalAssetsUsage,
    totalAssetsRights,
    totalUsersUsage,
    totalUsersRights,
    totalTeamsUsage,
    totalTeamsRights,
    totalOrganizationsUsage,
    totalOrganizationsRights,
    totalCreditsUsage,
    totalCreditsRights,
    totalCreditsRemaining,
    daysUntilLicenseExpires,
    organizationName,
    isVisibleLicenseExpireAlert,
    isPocLicense,
    fetchMyLicense,
    fetchLicenseSummary,
    hasAssetUsed,
    hasMyLicenseFeature,
    fetchStats,
    hasMyOrganizationModule,
    fetchMyOrganization,
    switchOrganization,
    hasMyOrganizationModuleCategory,
  }
})

// Need to be used outside the setup
export const useOrganizationStoreWithout = () => {
  return useOrganizationStore(store)
}
